import { ErrorHandler, Injectable, Provider } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import posthog from 'posthog-js';
import { environment } from 'src/environments/environment';
import { isLocalhost } from '../../../main';

/**
 * Custom ErrorHandler that sends unhandled exceptions to PostHog.
 * Works alongside your PosthogService (which initializes the base client).
 */
@Injectable({ providedIn: 'root' })
class PostHogErrorHandler implements ErrorHandler {
  public handleError(error: unknown): void {
    // Skip error tracking if PostHog is disabled in environment config
    if (!environment.postHogEnabled) {
      console.warn('[PostHogErrorHandler] Skipped: error tracking disabled');
      console.error(error);
      return;
    }

    const extractedError = this._extractError(error) || 'Unknown error';

    // Filter out noise before sending to PostHog
    if (this._shouldIgnoreError(extractedError)) {
      console.debug('[PostHogErrorHandler] Ignored:', extractedError);
      return;
    }

    // Run outside Angular zone to avoid triggering change detection
    runOutsideAngular(() => {
      try {
        if (posthog?.captureException) {
          posthog.captureException(extractedError);
        } else {
          // Fallback for older SDK versions
          posthog.capture('angular_error', {
            error: extractedError,
          });
        }
      } catch (err) {
        console.error('[PostHogErrorHandler] Failed to send error:', err);
      }
    });

    // Keep console logging for local debugging
    console.error(extractedError);
  }

  private _extractError(errorCandidate: unknown): unknown {
    // Unwrap Zone.js wrapped errors (Angular-specific)
    const error = tryToUnwrapZonejsError(errorCandidate);

    // Handle HTTP errors specially
    if (error instanceof HttpErrorResponse) {
      return extractHttpModuleError(error);
    }

    // Return string or error-like objects
    if (typeof error === 'string' || isErrorOrErrorLikeObject(error)) {
      return error;
    }

    return null;
  }

  private _shouldIgnoreError(error: unknown): boolean {
    // Convert error to string for pattern matching
    const errorString = this._errorToString(error);

    // Ignore ResizeObserver errors (common browser false positive)
    if (errorString.includes('ResizeObserver loop')) {
      return true;
    }

    // Ignore generic "Script error" (cross-origin without CORS)
    if (errorString === 'Script error' || errorString === 'Script error.') {
      return true;
    }

    // Ignore cancelled requests (user navigated away)
    if (
      errorString.includes('AbortError') ||
      errorString.includes('cancelled') ||
      errorString.includes('The user aborted a request')
    ) {
      return true;
    }

    // Ignore browser extension errors
    if (error instanceof Error && error.stack) {
      if (
        error.stack.includes('chrome-extension://') ||
        error.stack.includes('moz-extension://') ||
        error.stack.includes('safari-extension://')
      ) {
        return true;
      }
    }

    // Ignore HTTP errors that are expected/handled
    // Filter out ALL HTTP errors
    if (error instanceof HttpErrorResponse) {
      return true;
    }

    // Ignore errors from bots/crawlers
    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent.toLowerCase();
      if (
        userAgent.includes('bot') ||
        userAgent.includes('crawler') ||
        userAgent.includes('spider') ||
        userAgent.includes('googlebot') ||
        userAgent.includes('bingbot')
      ) {
        return true;
      }
    }

    return false;
  }

  private _errorToString(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof Error) {
      return error.message || error.toString();
    }

    if (error && typeof error === 'object' && 'message' in error) {
      return String(error.message);
    }

    return String(error);
  }
}

// Utility functions (same as PostHog doc)
function tryToUnwrapZonejsError(error: unknown): unknown | Error {
  return error && (error as { ngOriginalError: Error }).ngOriginalError
    ? (error as { ngOriginalError: Error }).ngOriginalError
    : error;
}

function extractHttpModuleError(error: HttpErrorResponse): string | Error {
  if (isErrorOrErrorLikeObject(error.error)) {
    return error.error;
  }

  if (
    typeof ErrorEvent !== 'undefined' &&
    error.error instanceof ErrorEvent &&
    error.error.message
  ) {
    return error.error.message;
  }

  if (typeof error.error === 'string') {
    return `Server returned code ${error.status} with body "${error.error}"`;
  }

  return error.message;
}

function isErrorOrErrorLikeObject(value: unknown): value is Error {
  if (value instanceof Error) return true;
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  return 'name' in value && 'message' in value && 'stack' in value;
}

// Angular Zone safe runner
declare const Zone: any;
const isNgZoneEnabled = typeof Zone !== 'undefined' && Zone.root?.run;

export function runOutsideAngular<T>(callback: () => T): T {
  return isNgZoneEnabled ? Zone.root.run(callback) : callback();
}

/**
 * Provider to register PostHogErrorHandler globally in Angular.
 */
export function providePostHogErrorHandler(): Provider {
  if (environment.postHogEnabled && !isLocalhost) {
    return {
      provide: ErrorHandler,
      useClass: PostHogErrorHandler,
    };
  }

  return {
    provide: ErrorHandler,
    useClass: ErrorHandler, // fallback to Angular default
  };
}
